import { useEffect, useRef, useState } from "react";
import { AspectRatio, Box, Button, Center, Container, Flex, Float, Heading, Image, Portal, Separator, Stack, Tag, Text, VStack } from "@chakra-ui/react";
import { LuExpand, LuMessageCircle, LuShoppingCart, LuYoutube } from "react-icons/lu";
import { FaShare } from "react-icons/fa";
import Slider from "react-slick";
import { Field } from "@/components/ui/field";
import { Dialog } from "@chakra-ui/react";
import SingleProductPriceLevel2 from "@/components/product/price/SingleProductPriceLevel2";
import TierProductPrice from "@/components/product/price/TierProductPrice";
import ShareProduct from "./Share";
import VariantOptionValues from "./VariantOptionValues";
import ProductFullscreenViewer from "./ProductFullscreenViewer";
import ProductMediaThumbnails from "./ProductMediaThumbnails";
import { getMediaSrc, useProductMedia } from "./ProductMediaContext";

const YoutubeIframe = ({ id, open, onClose }) => {
    return (
        <Dialog.Root lazyMount open={open}>
            <Portal>
                <Dialog.Backdrop />
                <Dialog.Positioner>
                    <Dialog.Content>
                        <Dialog.Body>
                            <AspectRatio ratio={4 / 3}>
                                <iframe
                                    id="ytplayer"
                                    type="text/html"
                                    width="100%"
                                    height="100%"
                                    src={`https://www.youtube.com/embed/${id}?autoplay=1`}
                                    frameBorder="0"
                                />
                            </AspectRatio>
                        </Dialog.Body>
                        <Dialog.Footer>
                            <Button variant="outline" onClick={onClose}>Đóng</Button>
                        </Dialog.Footer>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    );
};

const ReadMoreBox = ({ children }) => {
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [showButton, setShowButton] = useState(true);
    const divRef = useRef();

    useEffect(() => {
        setIsCollapsed(true);
    }, [children]);

    useEffect(() => {
        if (divRef.current?.clientHeight > 300) {
            setShowButton(true);
        } else {
            setShowButton(false);
            setIsCollapsed(false);
        }
    }, [divRef.current?.clientHeight]);

    return (
        <div style={{ maxHeight: isCollapsed ? 300 : 99999999, overflow: "hidden", position: "relative", paddingBottom: 40 }}>
            <div ref={divRef}>{children}</div>
            {showButton && (
                <div style={{ position: "absolute", bottom: 0, width: "100%", backgroundColor: "white", display: "flex", justifyContent: "center" }}>
                    {isCollapsed ? (
                        <Button variant="plain" onClick={() => setIsCollapsed(false)}>Xem thêm</Button>
                    ) : (
                        <Button variant="plain" onClick={() => setIsCollapsed(true)}>Thu gọn</Button>
                    )}
                </div>
            )}
        </div>
    );
};

const ProductCustom1 = ({ viewModel }) => {
    const { openFullscreen } = useProductMedia();

    const {
        id,
        data,
        selectedVariant,
        selectedOptions,
        productOptions,
        images,
        headingTitle,
        itemCaculatedPrice,
        ytPlayer,
        slickCurrentIndex,
        relatedProducts,
        showShare,
        settings,
        sliderRef,
        hasChatEntry,
        openChat,
        navigate,
        showAddToCart,
        onSelectOption,
        onSlideChange,
        goToSlide,
        setYtPlayer,
        setShowShare,
    } = viewModel;

    return (
        <div>
            <div style={{ paddingBottom: 50 }}>
                <div style={{ marginBottom: 32, width: "99%" }}>
                    <div style={{ position: "relative" }}>
                        <Slider {...settings} afterChange={onSlideChange} ref={sliderRef} dots={false}>
                            {(images || []).map(img => {
                                if (img.type === "video") {
                                    return (
                                        <AspectRatio bg="bg.muted" ratio={4 / 3} key={`video_${img.id || img.url}`}>
                                            <Center>
                                                <Image src={img.url} />
                                                <Float placement="middle-center" offsetX={16} offsetY={6}>
                                                    <Button size="lg" onClick={() => setYtPlayer({ open: true, url: img.video_url, id: img.id })}>
                                                        <LuYoutube size={64} />
                                                    </Button>
                                                </Float>
                                            </Center>
                                        </AspectRatio>
                                    );
                                }

                                return (
                                    <AspectRatio bg="bg.muted" ratio={4 / 3} key={`image_${img.id || img.url || img.md_url}`}>
                                        <div>
                                            <Center>
                                                <Image src={getMediaSrc(img)} fit="contain" />
                                            </Center>
                                            {(img.variant_titles?.length > 0 || img.variant) && (
                                                <Float placement="top-start" offsetX={16} offsetY={6}>
                                                    <Tag.Root>
                                                        <Tag.Label>{img.variant_titles?.[0] || img.variant?.variant_title}</Tag.Label>
                                                    </Tag.Root>
                                                </Float>
                                            )}
                                        </div>
                                    </AspectRatio>
                                );
                            })}
                        </Slider>

                        <Button
                            size="md"
                            variant="solid"
                            aria-label="Xem toàn màn hình"
                            title="Xem toàn màn hình"
                            onClick={() => openFullscreen(slickCurrentIndex)}
                            style={{
                                position: "absolute",
                                right: 12,
                                bottom: 12,
                                zIndex: 2,
                                borderRadius: 999,
                                minWidth: 44,
                                height: 44,
                                paddingInline: 0,
                                background: "rgba(255,255,255,0.92)",
                                color: "#111827",
                                border: "1px solid rgba(17,24,39,0.12)",
                                boxShadow: "0 6px 20px rgba(0,0,0,0.16)",
                            }}
                        >
                            <LuExpand size={20} />
                        </Button>
                    </div>

                    <ProductMediaThumbnails
                        images={images || []}
                        activeIndex={slickCurrentIndex}
                        onSelect={goToSlide}
                        containerStyle={{ marginTop: 16 }}
                    />
                </div>

                <Box>
                    <Stack>
                        <div>
                            {data.variants?.length > 1 && <Heading>{headingTitle}</Heading>}
                            {selectedVariant && (
                                <Text mt="1" fontSize="xl" fontWeight="700" color="red.500">
                                    {selectedVariant.price_list.length > 0
                                        ? `${Number(itemCaculatedPrice || 0).toLocaleString("vi-VN")}đ`
                                        : `${Number(selectedVariant.original_price || 0).toLocaleString("vi-VN")}đ`}
                                </Text>
                            )}
                        </div>

                        <Stack gap="4">
                            {data.variants?.length > 1 && (
                                <>
                                    {productOptions.map((x, index) => (
                                        <Field label={x.title} required key={x.id || index}>
                                            <VariantOptionValues
                                                option={x}
                                                optionIndex={index}
                                                selectedOption={selectedOptions[index]}
                                                variants={data.variants || []}
                                                onSelectOption={onSelectOption}
                                            />
                                        </Field>
                                    ))}
                                </>
                            )}

                            <div>
                                {selectedVariant && (
                                    <div>
                                        {selectedVariant.price_list.length > 0 ? (
                                            <TierProductPrice priceList={selectedVariant.price_list} />
                                        ) : (
                                            <SingleProductPriceLevel2
                                                price={selectedVariant.original_price}
                                                originalPrice={selectedVariant.original_price}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        </Stack>
                    </Stack>
                </Box>

                <Box>
                    <ReadMoreBox>
                        {(data.description ?? "").split("\n").map((line, index) => (
                            <span key={`line_${index}`}>
                                {line}
                                <br />
                            </span>
                        ))}
                    </ReadMoreBox>
                </Box>

                {(data.shopee_link || data.tiktok_shop_link) && (
                    <Box style={{ marginTop: 16 }}>
                        <VStack>
                            <Text>Bạn có thể mua sản phẩm này trên các sàn</Text>
                            {data.shopee_link && (
                                <Button variant="outline" onClick={() => { window.location = data.shopee_link; }}>
                                    Shopee
                                </Button>
                            )}

                            {data.tiktok_shop_link && (
                                <Button variant="outline" onClick={() => { window.location = data.tiktok_shop_link; }}>
                                    Tiktok shop
                                </Button>
                            )}
                        </VStack>
                    </Box>
                )}

                <div>
                    <Text>Sản phẩm khác</Text>
                </div>

                <div style={{ width: "100%", overflowX: "scroll", display: "flex", marginTop: 8 }}>
                    <div style={{ display: "flex", gap: 5, flexDirection: "row", flexWrap: "nowrap", scrollBehavior: "smooth" }}>
                        {relatedProducts.map(product => (
                            <div key={product.id} style={{ width: 150 }}>
                                <AspectRatio bg="bg.muted" ratio={4 / 3} onClick={() => navigate(`/product/${product.id}`)}>
                                    <div>
                                        <Center>
                                            <Image src={product.images[0].md_url} />
                                        </Center>
                                    </div>
                                </AspectRatio>
                            </div>
                        ))}
                    </div>
                </div>

                <Box style={{ marginTop: 16 }}>
                    <Flex gap="2" justify="center">
                        {hasChatEntry && (
                            <Button
                                variant="outline"
                                onClick={() => openChat({
                                    chatAttachment: {
                                        type: "product",
                                        title: data?.name,
                                        meta: { product_id: data?.id, variant_id: selectedVariant?.id },
                                        url: data?.images?.[0]?.url,
                                    },
                                })}
                            >
                                <LuMessageCircle /> Chat
                            </Button>
                        )}

                        <Button variant="outline" onClick={() => setShowShare(true)}>
                            <FaShare /> Chia sẻ
                        </Button>
                    </Flex>
                </Box>
            </div>

            <Container maxW="md" style={{ position: "fixed", bottom: 0, left: 0, right: 0, backgroundColor: "white", padding: 0 }}>
                <Box bg="bg" shadow="md" borderRadius="md">
                    {data.out_of_stock && (
                        <div style={{ paddingBottom: 8 }}>
                            <Center color="red.400" style={{ padding: 8 }}>
                                Sản phẩm này tạm thời hết hàng
                            </Center>
                            {hasChatEntry && (
                                <Center fontSize="xl">
                                    <Button
                                        variant="outline"
                                        onClick={() => openChat({
                                            chatAttachment: {
                                                type: "product",
                                                title: data?.name,
                                                meta: { product_id: data?.id, variant_id: selectedVariant?.id },
                                                url: data?.images?.[0]?.url,
                                            },
                                        })}
                                    >
                                        <LuMessageCircle /> Nhận thông báo khi có hàng
                                    </Button>
                                </Center>
                            )}
                        </div>
                    )}

                    {!data.out_of_stock && (
                        <Flex gap="2" style={{ height: 50 }}>
                            <div style={{ flex: 0.4 }}>
                                <Center h="100%" px="2">
                                    {selectedVariant && (
                                        <Text fontWeight="600" color="red.500" fontSize="md" textAlign="center">
                                            {selectedVariant.price_list.length > 0
                                                ? `${Number(itemCaculatedPrice || 0).toLocaleString("vi-VN")}đ`
                                                : `${Number(selectedVariant.original_price || 0).toLocaleString("vi-VN")}đ`}
                                        </Text>
                                    )}
                                </Center>
                            </div>

                            <div>
                                <Center fontSize="xl" h="100%">
                                    <Separator orientation="vertical" height="4" />
                                </Center>
                            </div>

                            <div style={{ flex: 0.6, backgroundColor: "red.200" }}>
                                <Center fontSize="xl" h="100%" onClick={() => showAddToCart(data.id, selectedVariant?.id)}>
                                    <Button variant="solid">
                                        <LuShoppingCart /> Thêm vào giỏ hàng
                                    </Button>
                                </Center>
                            </div>
                        </Flex>
                    )}
                </Box>
            </Container>

            <YoutubeIframe
                open={ytPlayer.open}
                id={ytPlayer.id}
                onClose={() => setYtPlayer({ open: false, url: "", id: "" })}
            />
            <ShareProduct open={showShare} id={id} onOpenChange={() => setShowShare(false)} />
            <ProductFullscreenViewer
                onOpenVideo={(media) => setYtPlayer({ open: true, url: media?.video_url || "", id: media?.id || "" })}
            />
        </div>
    );
};

export default ProductCustom1;
